/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// Light theme seam so boot (`app.ts`) can apply the persisted theme before first paint
// without dragging the lazy-loaded Settings page into the app-shell chunk.

import { getDeviceConfig } from '@/device/deviceConfig'
import { queryParam } from '@/util/queryParams'

/** localStorage key for the persisted theme choice. */
export const THEME_KEY = 'theme'

/** The three legal theme choices. */
export type ThemeChoice = 'dark' | 'light' | 'system'

// Dark until the customer picks Light or System in settings; the persisted pick wins.
/** The default theme when nothing is persisted or the value is malformed. */
export const DEFAULT_THEME: ThemeChoice = 'dark'

/** Attribute the effective (resolved) theme is written under, on `<html>`. */
export const THEME_ATTR = 'data-bb-theme'

/**
 * Read the persisted choice. Returns the {@link DEFAULT_THEME} on any failure.
 */
export function readTheme(): ThemeChoice {
    try {
        const raw = localStorage.getItem(THEME_KEY)
        if (raw === 'dark' || raw === 'light' || raw === 'system') return raw
    } catch {
        /* ignore */
    }
    return DEFAULT_THEME
}

/**
 * Persist the choice. Writes the value verbatim so `readTheme()` always
 * returns the exact user pick — important for `'system'` where losing the
 * choice would look like a bug.
 */
export function writeTheme(next: ThemeChoice): void {
    try {
        localStorage.setItem(THEME_KEY, next)
    } catch {
        /* private-mode Safari — ignore. */
    }
}

/** False on a device without light mode, where every choice resolves to dark. */
export function lightModeAvailable(): boolean {
    return getDeviceConfig().features.lightMode
}

/**
 * Resolve a stored choice to a concrete `'dark' | 'light'` value.
 * `'system'` is resolved via `matchMedia('(prefers-color-scheme: light)')`;
 * missing `matchMedia` falls back to dark. A device without light mode is
 * always dark.
 */
export function resolveTheme(choice: ThemeChoice): 'dark' | 'light' {
    if (!lightModeAvailable()) return 'dark'
    if (choice === 'dark' || choice === 'light') return choice
    try {
        if (typeof matchMedia === 'function') {
            return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
        }
    } catch {
        /* ignore */
    }
    return 'dark'
}

/**
 * Write the resolved theme onto `<html data-bb-theme='...'>`.
 */
export function applyTheme(choice: ThemeChoice): void {
    try {
        // try/catch wraps the whole call to cover non-browser hosts
        // (SSR / test edge) where `document` itself is missing.
        document.documentElement.setAttribute(THEME_ATTR, resolveTheme(choice))
    } catch {
        /* ignore — no document (SSR/test edge) */
    }
}

/**
 * Read the `?theme=` boot override, if present and valid.
 */
function themeOverrideFromUrl(): ThemeChoice | null {
    const raw = queryParam('theme')
    return raw === 'dark' || raw === 'light' || raw === 'system' ? raw : null
}

/**
 * Resolve the theme to apply at boot: the `?theme=` override, if valid, else
 * the persisted choice. A valid override is also persisted via
 * {@link writeTheme}, so the pick sticks on later loads even once the param
 * is gone. Use this in place of {@link readTheme} for the boot paint —
 * everywhere else (settings UI, {@link watchSystemTheme}) should keep
 * reading the persisted choice directly.
 */
export function resolveBootTheme(): ThemeChoice {
    const override = themeOverrideFromUrl()
    if (override === null) return readTheme()
    writeTheme(override)
    return override
}

/**
 * Live-follow the OS color scheme for the whole app lifetime. While the
 * persisted choice is `'system'`, an OS light/dark flip re-applies the resolved theme without a reload; an
 * explicit `'dark'`/`'light'` pick is ignored so the override still wins.
 *
 * Returns a disposer that removes the listener. Guarded against non-browser
 * hosts (SSR / test edge) where `matchMedia` is missing.
 */
export function watchSystemTheme(): () => void {
    try {
        if (typeof matchMedia !== 'function') return () => {}
        const mql = matchMedia('(prefers-color-scheme: light)')
        const onChange = () => {
            if (readTheme() === 'system') applyTheme('system')
        }
        mql.addEventListener('change', onChange)
        return () => mql.removeEventListener('change', onChange)
    } catch {
        return () => {}
    }
}
